import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, TemplateRef } from '@angular/core';

import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';

import { FacetSelectionType } from '../../../profile-facets/entities/facet-request.entity';
import { ShIconModule } from '../icon/icon.module';
import { ShFacet, ShFacetValue, ShSelectableFacetValue, ShSelectedFacetValue } from './entities/facet.entity';
import { ShFacetValueLabelPipe } from './pipes/facet-value-label.pipe';

export * from './entities/facet.entity';

/** What {@link ShFacetComponent.bodyTemplate} is handed: the facet it is drawing, as `$implicit`. */
export interface ShFacetBodyContext {
    $implicit: ShFacet;
}
@Component({
    standalone: true,
    selector: 'sh-facet',
    templateUrl: './facet.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShIconModule, TranslateModule, ShFacetValueLabelPipe]
})
export class ShFacetComponent extends BaseContainer implements OnInit {
    private readonly blockShowMore$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public readonly noValueKey: string = 'overrides.facets.no-value';
    public facet$: ReplaySubject<ShFacet | undefined> = new ReplaySubject(1);
    public showLoadMore = false;
    public ShFacetSelectionType: typeof FacetSelectionType = FacetSelectionType;

    @Input() set facet(facet: ShFacet | undefined) {
        this.facet$.next(facet);
    }

    @Input() showLabel = true;

    @Input() collapsible = false;
    @Input() expanded = false;

    @Input() showMoreMsg = 'Show more';
    @Input() set blockShowMore(blockShowMore: boolean) {
        this.blockShowMore$.next(blockShowMore);
    }

    // Do not remove. Used to generate different ids for dialog and list facet values
    // Removing this will trigger unwanted behaviour.
    @Input() facetDialogOpen = false;

    /**
     * HFBD-6: an optional body for the facet, replacing its list of values and the show-more link below it.
     * The facet keeps everything around that body — its title, the collapse chevron, and the reorder handle
     * the facet list wraps it in — so a filter needing a shape of its own still sits among the others.
     *
     * ⚠ **Additive on purpose.** A caller that passes nothing renders exactly what it rendered before, which
     * is what lets the column-header dropdown and the *Toon meer* dialog opt out by construction.
     *
     * The show-more link goes with the value list rather than staying: a caller supplying its own body owns
     * its own paging, and the *Toon meer* dialog replaces **every** selected value of a facet at once, which
     * is wrong for any body whose selection is a set of values rather than one.
     */
    @Input() bodyTemplate: TemplateRef<ShFacetBodyContext> | null = null;

    @Output() selectionChanged: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();
    @Output() valueSelected: EventEmitter<ShSelectedFacetValue> = new EventEmitter<ShSelectedFacetValue>();

    @Output() loadMore: EventEmitter<ShFacet> = new EventEmitter<ShFacet>();
    @Output() expandedToggled: EventEmitter<void> = new EventEmitter<void>();

    trackByLabel(index: number, facetValue: ShFacetValue): string {
        return facetValue.label;
    }

    ngOnInit(): void {
        this.initShouldShowMoreListener();
    }

    private initShouldShowMoreListener(): void {
        combineLatest([this.facet$, this.blockShowMore$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([facet, blockShowMore]) => {
                if (!facet || blockShowMore) {
                    this.showLoadMore = false;
                    return;
                }

                if (facet.hasMoreValues) {
                    this.showLoadMore = true;
                    return;
                }

                // If facet is included in api response but without values, we force the 'load more' button to be visible
                if (!facet.hasMoreValues && facet.values.length === 0) {
                    this.showLoadMore = true;
                    return;
                }

                this.showLoadMore = false;
            });
    }

    public onShowMore(facet: ShFacet): void {
        this.loadMore.emit(facet);
    }

    public onToggleExpanded(): void {
        this.expandedToggled.emit();
    }

    public onSelectionChange(facet: ShFacet, facetValue: ShFacetValue): void {
        const newValues: ShSelectableFacetValue[] = facet.values.map(fv => {
            if (fv.value === facetValue.value) {
                return { ...fv, selected: !fv.selected };
            }
            return fv;
        });

        const newFacet: ShFacet = { ...facet, values: newValues };

        this.selectionChanged.emit(newFacet);

        const valueSelected: ShSelectedFacetValue = {
            facetLabel: facet.label,
            key: facet.key,
            valueLabel: facetValue.label,
            value: facetValue.value,
            missing: facetValue.missing,
            selectionType: facet.selectionType,
            count: facetValue.count
        };

        this.valueSelected.emit(valueSelected);
    }
}
